import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import useKlassesMap from '@/data/hook/shared/useKlassesMap';
import useAttendancePendencyColumns, {
  AttendancePendencyColumnKey,
} from '@/data/hook/shared/pendencies/tables/useAttendancePendencyColumns';
import { ScheduledLessonOrdering } from '@/data/services/scheduledLessonsServices';
import { OrderingKey } from '@/models/Filter';
import ScheduledLesson from '@/models/ScheduledLesson';
import { SmartTable, SmartTableProps } from '@/components/ui/daisy';

import { ScheduledLessonWithKlass } from './types';

type AttendancePendenciesTableProps = Omit<
  SmartTableProps<ScheduledLessonWithKlass, AttendancePendencyColumnKey>,
  'columns' | 'getRowKey' | 'data'
> & {
  data: ScheduledLesson[];
  ordering?: ScheduledLessonOrdering;
  onToggleOrdering: (field: OrderingKey<ScheduledLessonOrdering>) => void;
};

export default function AttendancePendenciesTable({
  data,
  ordering,
  onToggleOrdering,
  className,
  ...smartTableProps
}: AttendancePendenciesTableProps) {
  const columns = useAttendancePendencyColumns({
    ordering,
    onToggleOrdering,
  });

  const klassIds = useMemo(() => data.map(({ klass }) => klass), [data]);

  const { klassesMap, isLoading: isLoadingKlass } = useKlassesMap({ klassIds });

  const rows = useMemo(
    () =>
      data.map(scheduledLesson => ({
        ...scheduledLesson,
        klass: {
          ...klassesMap[scheduledLesson.klass],
          id: scheduledLesson.klass,
          isLoading: isLoadingKlass,
        },
      })),
    [data, klassesMap, isLoadingKlass],
  );

  return (
    <SmartTable
      columns={columns}
      data={rows}
      getRowKey={scheduledLesson => scheduledLesson.id}
      className={twMerge('table-fixed', className)}
      {...smartTableProps}
    />
  );
}
